import React, { useEffect, useState } from 'react';
import './fieldBuilder.css';
import { useAuth } from '../../../context/AuthContext';
import { ownsTemplate } from '../../../lib/templatePermissions';
import { useNavigate } from 'react-router-dom';
import { templatesAPI } from '../../../api/templates';
import {
  templateStatusConfig, normalizeTemplateStatus, isTemplateEditable, canCreateNewVersion,
} from '../../../lib/templateStatus';
import {
  Plus, Edit2, Copy, Archive, Star, Info, CheckCircle,
  FileText, Trash2, X, Eye, GitBranch,
} from 'lucide-react';
import { toast } from 'sonner';

// #Fase0-B3 — filtro por estatus. Las archivadas siguen fuera de esta lista
// (se consultan en Archivo); "retirada" es nuevo (C6).
const STATUS_FILTERS = [
  { id: 'todas',    label: 'Todas' },
  { id: 'activa',   label: 'Activas' },
  { id: 'borrador', label: 'Borradores' },
  { id: 'retirada', label: 'Retiradas' },
];

function NewTemplateModal({ onSave, onClose }) {
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-soem-navy">
          <h3 className="font-bold text-white">Nueva Plantilla</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label htmlFor="f-nombre-de-la-plantilla" className="soem-label block mb-1.5">Nombre de la plantilla <span className="text-red-500">*</span></label>
            <input id="f-nombre-de-la-plantilla" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
              placeholder="Ej: Evaluación de Seguridad v2" value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="tmpl-name-input" />
          </div>
          <div>
            <label htmlFor="f-descripción-opcional" className="soem-label block mb-1.5">Descripción (opcional)</label>
            <textarea id="f-descripción-opcional" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary resize-none" rows={3}
              placeholder="Describe el propósito de esta plantilla..." value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })} data-testid="tmpl-desc-input" />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={saving || !form.name}
              className="soem-btn-primary flex-1 py-2.5 text-sm disabled:opacity-60" data-testid="tmpl-save-btn">
              {saving ? 'Creando...' : 'Crear y abrir constructor'}
            </button>
            <button type="button" onClick={onClose} className="soem-btn-secondary flex-1 py-2.5 text-sm">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TemplatesList() {
  const navigate = useNavigate();
  const { activeUser, user } = useAuth();
  const actor = activeUser || user;
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [statusFilter, setStatusFilter] = useState('todas');

  const load = async () => {
    setLoading(true);
    try {
      const r = await templatesAPI.list();
      // Hide archived templates from the main listing — they are
      // visible/restorable through the Archive section.
      const items = (r.data || []).filter(t => normalizeTemplateStatus(t.status) !== 'archivada' && !t.is_archived);
      setTemplates(items);
    }
    catch { toast.error('Error al cargar plantillas'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (form) => {
    try {
      const r = await templatesAPI.create(form);
      toast.success('Plantilla creada');
      setShowNew(false);
      navigate(`/dashboard/templates/${r.data.id}/edit`);
    } catch { toast.error('Error al crear plantilla'); }
  };

  const handleDuplicate = async (t) => {
    if (!ownsTemplate(actor, t) && !(actor?.role === 'lider_proyecto' && t.is_official)) return;
    try {
      const r = await templatesAPI.duplicate(t.id);
      toast.success(`"${r.data.name}" creada`);
      load();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al duplicar');
    }
  };

  const handleArchive = async (t) => {
    if (!ownsTemplate(actor, t)) return;
    if (!window.confirm(`¿Archivar "${t.name}"?`)) return;
    try {
      await templatesAPI.archive(t.id);
      toast.success('Plantilla archivada');
      load();
    } catch (err) {
      // #Fase0-B3 / C6 — la API rechaza archivar la predeterminada con un
      // 400 que explica qué hacer; antes se mostraba un error genérico.
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al archivar');
    }
  };

  const handleNewVersion = async (t) => {
    try {
      const r = await templatesAPI.newVersion(t.id);
      toast.success(`Borrador ${r.data?.version ? `v${r.data.version} ` : ''}de "${t.name}" creado`);
      navigate(`/dashboard/templates/${r.data.id}/edit`);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al crear la nueva versión');
    }
  };

  const handleActivate = async (t) => {
    // #Fase0-B3 / C6 — si es una versión nueva de una plantilla activa, al
    // activarla la API retira a la anterior. Se avisa antes de confirmar.
    let msg = `¿Activar la plantilla "${t.name}"?\n\nLas evaluaciones nuevas podrán usar esta plantilla.`;
    if (t.parent_id) {
      const parent = templates.find(x => x.id === t.parent_id);
      if (!parent || normalizeTemplateStatus(parent.status) === 'activa') {
        msg += `\n\nLa versión anterior${parent?.version ? ` (v${parent.version})` : ''} quedará RETIRADA: ya no se ofrecerá para evaluaciones nuevas. `
          + 'Las evaluaciones que ya la usan (incluidas las que están en curso) siguen con su versión.';
      }
    }
    if (!window.confirm(msg)) return;
    try {
      await templatesAPI.activate(t.id);
      toast.success('Plantilla activada');
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al activar plantilla');
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`¿Eliminar permanentemente "${t.name}"?\nEsta acción no se puede deshacer.`)) return;
    try {
      await templatesAPI.delete(t.id);
      toast.success(`"${t.name}" eliminada`);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al eliminar plantilla');
    }
  };

  const handleSetDefault = async (t) => {
    // #Fase2-F9 — Un default con dueño no puede asignarse a todos los proyectos.
    if (actor?.role !== 'super_admin' || t.owner_id) return;
    try {
      await templatesAPI.setDefault(t.id);
      toast.success(`"${t.name}" establecida como plantilla por defecto`);
      load();
    } catch { toast.error('Error'); }
  };

  const visibleTemplates = statusFilter === 'todas'
    ? templates
    : templates.filter(t => normalizeTemplateStatus(t.status) === statusFilter);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-list">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Constructor de Evaluaciones</h1>
          <p className="text-slate-500 text-sm mt-1">Gestiona las plantillas de criterios de evaluación</p>
        </div>
        <button onClick={() => setShowNew(true)} className="soem-btn-primary px-4 py-2 text-sm flex items-center gap-2" data-testid="new-template-btn">
          <Plus size={16} /> Nueva Plantilla
        </button>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 flex items-start gap-3">
        <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-blue-800 text-sm">
          <strong>Las plantillas en borrador</strong> pueden editarse libremente.
          Para cambiar una plantilla activa crea una <strong>nueva versión</strong>: al activarla,
          la anterior queda <strong>retirada</strong> y las evaluaciones que ya la usan siguen con ella.
        </p>
      </div>

      <div className="flex gap-1.5 flex-wrap" data-testid="template-status-filter">
        {STATUS_FILTERS.map(f => (
          <button key={f.id} onClick={() => setStatusFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${statusFilter === f.id ? 'bg-soem-primary text-white border-soem-primary' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}>
            {f.label}
            <span className="ml-1 opacity-70">
              {f.id === 'todas' ? templates.length : templates.filter(t => normalizeTemplateStatus(t.status) === f.id).length}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="soem-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" />
          </div>
        ) : visibleTemplates.length === 0 && templates.length > 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">No hay plantillas con este estatus.</div>
        ) : templates.length === 0 ? (
          <div className="p-16 text-center">
            <FileText size={48} className="text-slate-200 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">No hay plantillas aún.</p>
            <p className="text-slate-400 text-sm mt-1">Crea tu primera plantilla de evaluación.</p>
            <button onClick={() => setShowNew(true)} className="soem-btn-primary px-5 py-2 text-sm mt-5 inline-flex items-center gap-2">
              <Plus size={14} /> Crear plantilla
            </button>
          </div>
        ) : (
          <table className="template-table w-full text-sm">
            <thead>
              <tr className="soem-table-header">
                <th className="px-5 py-3 text-left">Nombre</th>
                <th className="px-5 py-3 text-left">Secciones</th>
                <th className="px-5 py-3 text-left">Preguntas</th>
                <th className="px-5 py-3 text-left">Estado</th>
                <th className="px-5 py-3 text-left">Actualizada</th>
                <th className="px-5 py-3 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibleTemplates.map(t => (
                <tr key={t.id} className="soem-table-row" data-testid={`template-row-${t.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-soem-navy">{t.name}</span>
                      {t.version && <span className="text-xs text-slate-400 font-mono">v{t.version}</span>}
                      {t.is_default && !t.owner_id && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full font-bold border border-yellow-200 flex items-center gap-1">
                          <Star size={9} /> Por defecto
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">{t.scope === 'lider' ? 'Del líder' : 'Global'}</p>
                    {t.description && <p className="text-slate-400 text-xs mt-0.5 truncate max-w-xs">{t.description}</p>}
                  </td>
                  <td data-label="Secciones" className="px-5 py-3 text-slate-600 font-mono">
                    {t.sections_count ?? t.section_count ?? t.sections?.length ?? 0}
                  </td>
                  <td data-label="Preguntas" className="px-5 py-3 text-slate-600 font-mono">
                    {t.questions_count ?? t.question_count ?? 0}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${templateStatusConfig(t.status).classes}`}>
                      {templateStatusConfig(t.status).label}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-400 text-xs">
                    {t.updated_at ? new Date(t.updated_at).toLocaleDateString('es-MX') : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      {normalizeTemplateStatus(t.status) !== 'archivada' && (
                        <button onClick={() => navigate(`/dashboard/templates/${t.id}/edit`)}
                          className="p-1.5 text-slate-400 hover:text-soem-primary rounded"
                          title={isTemplateEditable(t.status) ? 'Editar' : 'Ver (solo lectura)'} data-testid={`edit-${t.id}`}>
                          {isTemplateEditable(t.status) ? <Edit2 size={14} /> : <Eye size={14} />}
                        </button>
                      )}
                      {ownsTemplate(actor, t) && canCreateNewVersion(t.status) && (
                        <button onClick={() => handleNewVersion(t)}
                          className="p-1.5 text-slate-400 hover:text-soem-primary rounded" title="Nueva versión" data-testid={`new-version-${t.id}`}>
                          <GitBranch size={14} />
                        </button>
                      )}
                      {ownsTemplate(actor, t) && isTemplateEditable(t.status) && (
                        <button onClick={() => handleActivate(t)}
                          className="p-1.5 text-slate-400 hover:text-green-600 rounded" title="Activar plantilla" data-testid={`activate-${t.id}`}>
                          <CheckCircle size={14} />
                        </button>
                      )}
                      {(ownsTemplate(actor, t) || (actor?.role === 'lider_proyecto' && t.is_official)) && <button onClick={() => handleDuplicate(t)}
                        className="p-1.5 text-slate-400 hover:text-soem-primary rounded" title={t.is_official ? 'Duplicar plantilla oficial' : 'Duplicar'} data-testid={`dup-${t.id}`}>
                        <Copy size={14} />
                      </button>}
                      {actor?.role === 'super_admin' && !t.owner_id &&
                        normalizeTemplateStatus(t.status) === 'activa' && !t.is_default && (
                        <button onClick={() => handleSetDefault(t)}
                          className="p-1.5 text-slate-400 hover:text-yellow-600 rounded" title="Marcar como por defecto">
                          <Star size={14} />
                        </button>
                      )}
                      {ownsTemplate(actor, t) && normalizeTemplateStatus(t.status) !== 'archivada' && (
                        <button onClick={() => handleArchive(t)}
                          className="p-1.5 text-slate-400 hover:text-red-500 rounded" title="Archivar" data-testid={`archive-${t.id}`}>
                          <Archive size={14} />
                        </button>
                      )}
                      {ownsTemplate(actor, t) &&
                        ['archivada', 'borrador'].includes(normalizeTemplateStatus(t.status)) && (
                        <button onClick={() => handleDelete(t)}
                          className="p-1.5 text-slate-400 hover:text-red-600 rounded" title="Eliminar permanentemente" data-testid={`delete-${t.id}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNew && <NewTemplateModal onSave={handleCreate} onClose={() => setShowNew(false)} />}
    </div>
  );
}
